<template>
  <v-menu v-model="open" :close-on-content-click="false" location="bottom start">
    <template #activator="{ props: activatorProps }">
      <v-text-field
        v-bind="activatorProps"
        class="date-field"
        :model-value="modelValue ?? ''"
        :placeholder="placeholder"
        :aria-label="ariaLabel"
        :clearable="clearable"
        prepend-inner-icon="mdi-calendar"
        variant="plain"
        density="compact"
        hide-details
        readonly
        @click:clear="emit('update:modelValue', null)" />
    </template>
    <v-date-picker
      :model-value="pickerValue"
      :min="min"
      :first-day-of-week="1"
      color="#ea7801"
      hide-header
      show-adjacent-months
      @update:model-value="select" />
  </v-menu>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';

/** A calendar date as `YYYY-MM-DD`, without time or timezone. */
type IsoDate = string;

const props = withDefaults(defineProps<{
  modelValue: IsoDate | null;
  /** Earliest selectable date. */
  min?: IsoDate;
  clearable?: boolean;
  placeholder?: string;
  ariaLabel?: string;
}>(), {
  min: undefined,
  clearable: false,
  placeholder: 'Edit date',
  ariaLabel: 'Date',
});

const emit = defineEmits<{
  'update:modelValue': [value: IsoDate | null];
}>();

const open = ref(false);

const pad = (value: number) => String(value).padStart(2, '0');

// Local calendar dates on both sides: going through `toISOString()` would shift the day in
// timezones east of UTC.
const toIsoDate = (date: Date): IsoDate => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

const fromIsoDate = (value: IsoDate): Date | null => {
  const [year, month, day] = value.split('-').map(Number);
  return year && month && day ? new Date(year, month - 1, day) : null;
};

const pickerValue = computed(() => (props.modelValue ? fromIsoDate(props.modelValue) : null));

const select = (value: unknown) => {
  if (value instanceof Date) {
    emit('update:modelValue', toIsoDate(value));
  }
  open.value = false;
};
</script>

<style lang="scss" scoped>
.date-field {
  min-width: 160px;
}
</style>
